<script setup lang="ts">
import Button from '~/components/design-system/button.vue';
import GenericError from '~/components/user-interface/generic-error.vue';
import { useSiteStore } from '~/stores/site.store';
import { globalKeys } from 'assets/data/global-keys';
import { CheckCircleIcon } from '@heroicons/vue/24/outline';
import type { IFormBuilderReference } from '~/interfaces/form/formBuilder';

const { $authService, $storageService } = useServices();
const { $trackEvent } = useNuxtApp();
const site = useSiteStore();

const formLoading = ref<boolean>(false);
const form = ref<IFormBuilderReference>(null);
const errorCode = ref<string>('');
const errorRef = ref<string>('');

const model = ref({
  password: '',
  passwordConfirmation: '',
});

const navigateToSuccess = () => {
  site.activateModal('sumSubPasswordResetLoggedOutChangePasswordSuccess');
};

const confirm = async () => {
  errorCode.value = '';
  formLoading.value = true;
  const password = model.value['password'];
  const confirmPassword = model.value['passwordConfirmation'];

  try {
    const data = await $authService.passwordResetLoggedIn({
      password,
      confirmPassword,
    });

    if (data?.isSuccessful) {
      $storageService.removeCookie({
        key: globalKeys.authKeys.passwordResetNumber,
      });
      $trackEvent('Account', 'MY_ACCOUNT_PASSWORD_UPDATE_ACTION');
      navigateToSuccess();
    } else {
      errorCode.value = String(data?.error?.displayCode ?? 'general');
      errorRef.value = String(data?.error?.code ?? '');
    }
  } catch (error) {
    errorCode.value = String(error?.error?.displayCode ?? 'general');
    errorRef.value = String(error?.error?.code ?? '');
  } finally {
    formLoading.value = false;
  }
};
</script>
<template>
  <div class="p-4 flex flex-col gap-2">
    <p class="text-xs text-dark-900 dark:text-light-50 py-1">
      {{ $t('enter-new-password') }}
    </p>
    <UserInterfaceDynamicFormBuilder
      ref="form"
      template-name="update password"
      :model-value="model"
      :should-auto-focus="false"
      @loaded="formLoading = false"
    >
      <template v-if="!form?.isEmpty" #form-actions="formActionsProps">
        <div
          v-if="
            formActionsProps?.form?.validated &&
            !formActionsProps?.form?.invalid
          "
          class="flex items-center gap-2 mt-2"
        >
          <CheckCircleIcon
            class="dark:text-primary-blue-500 text-primary-blue-400 icon-success--border w-5"
          />
          <span class="text-xs font-light text-base-priority">
            {{ $t('Passwords-must-match-and-be-valid') }}
          </span>
        </div>
      </template>
    </UserInterfaceDynamicFormBuilder>
  </div>
  <div v-if="errorCode" class="mb-0 py-4 flex justify-center">
    <GenericError
      class="max-w-72 mx-auto"
      state="danger"
      :reference-code="errorRef"
    >
      {{ $t(`jpc-update-password-error-${errorCode}`) }}
    </GenericError>
  </div>

  <div class="p-4 bg-base text-sm flex gap-2 flex-row justify-center">
    <Button
      element-name="update-password-confirm"
      class="capitalize w-full justify-center text-sm"
      rounding="lg"
      type="tertiary"
      @click="site.activateModal('sumSubPasswordResetLoggedOutLivenessCheck')"
    >
      {{ $t('back') }}
    </Button>
    <Button
      element-name="update-password-confirm"
      class="capitalize w-full justify-center text-sm"
      :disabled="!(form?.validated && !form?.invalid)"
      rounding="lg"
      @click="confirm"
    >
      {{ $t('update-password') }}
    </Button>
  </div>
  <LazyUserInterfaceGenericLoader v-if="formLoading" container />
</template>
